import React from 'react';
import useAxios, { UseAxiosResult } from 'axios-hooks';
import { AxiosError } from 'axios';
import { durationEnds, parseISO8601 } from '../utils';

interface UseRejectVisitProps {
  rejectAllowedForWidget?: boolean;
  durationRejectConstraint?: string;
  divisionId?: string | null;
  visitId?: string;
  date?: Date | string;
}

interface UseRejectVisitReturn {
  isRejectAllowed: () => boolean;
  success: boolean;
  error?: AxiosError<unknown>;
  loading: boolean;
  rejectVisit: UseAxiosResult[1];
  resetError: () => void;
}

export const useRejectVisit = ({
  rejectAllowedForWidget,
  durationRejectConstraint,
  divisionId,
  visitId,
  date,
}: UseRejectVisitProps): UseRejectVisitReturn => {
  const [error, setError] = React.useState<AxiosError<unknown>>();
  const resetError = React.useCallback(() => {
    setError(undefined);
  }, []);
  const isRejectAllowed = React.useCallback(() => {
    if (!rejectAllowedForWidget || !durationRejectConstraint || !date) {
      return false;
    }

    return (
      durationEnds(parseISO8601(durationRejectConstraint), new Date()) <
      new Date(date)
    );
  }, [date, durationRejectConstraint, rejectAllowedForWidget]);

  const [response, rejectVisit] = useAxios(
    {
      url: `/booking-widget/client/visit/${visitId}/reject`,
      method: 'POST',
      data: {
        ...(divisionId ? { divisionId } : {}),
      },
    },
    { manual: true },
  );

  React.useEffect(() => {
    setError(response.error);
  }, [response.error]);

  return {
    isRejectAllowed,
    success: 'data' in response && !response.error && !response.loading,
    error,
    loading: response.loading,
    rejectVisit,
    resetError,
  };
};
